/**
 * useProjectBusyStore -- Lightweight Zustand store bridging the per-project
 * `isAgentBusy` flag (from useProjectChat) to the sidebar (CodeProjectSidebar /
 * ProjectRow) so the "working" status bullet can update in real-time for the
 * active project without waiting for a projects-list refetch.
 *
 * Shape: { [projectId]: boolean }
 *
 * Written by useProjectChat.  Read by CodeProjectSidebar.
 */

import { create } from 'zustand';

interface ProjectBusyState {
  /** Map of projectId -> isAgentBusy. Only set for projects with an active useProjectChat instance. */
  busyMap: Record<string, boolean>;
  /** Update a project's busy state. */
  setBusy: (projectId: string, busy: boolean) => void;
  /** Remove a project from the map (cleanup on unmount). */
  clear: (projectId: string) => void;
}

export const useProjectBusyStore = create<ProjectBusyState>((set) => ({
  busyMap: {},
  setBusy: (projectId, busy) =>
    set((state) => {
      if (state.busyMap[projectId] === busy) return state; // no-op avoids re-render
      return { busyMap: { ...state.busyMap, [projectId]: busy } };
    }),
  clear: (projectId) =>
    set((state) => {
      if (!(projectId in state.busyMap)) return state;
      const next = { ...state.busyMap };
      delete next[projectId];
      return { busyMap: next };
    }),
}));
